/**
 * Debug logging utility that only logs in development mode or when explicitly enabled.
 * Prevents sensitive data (API keys, internal state) from being exposed in production.
 *
 * Usage:
 *   import { debugLog } from '@/lib/debug';
 *   debugLog('[Component] Message', data);
 *
 * Enable in production:
 *   localStorage.setItem('debug', 'true');
 */

/**
 * Check if debug logging is enabled
 * @returns true if in development mode or debug flag is set in localStorage
 */
function isDebugEnabled(): boolean {
  // In test environment, disable debug logging by default
  // (can be enabled via localStorage if needed)
  if (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') {
    // Check localStorage flag for test debugging
    if (typeof window !== 'undefined' && window.localStorage) {
      try {
        return window.localStorage.getItem('debug') === 'true';
      } catch {
        return false;
      }
    }
    return false;
  }

  // Always enabled in development mode. We reference `import.meta.env` directly
  // (Vite always defines it; ts-jest's `jest-import-meta-transformer` rewrites
  // it to `process.env`) and never a bare `typeof import.meta`, which the
  // transformer can't rewrite and which crashes the CommonJS test build.
  const isDev =
    import.meta.env?.DEV ||
    (typeof process !== 'undefined' && process.env?.NODE_ENV === 'development');
  if (isDev) {
    return true;
  }

  // Check localStorage flag for production debugging
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      return window.localStorage.getItem('debug') === 'true';
    } catch {
      // localStorage access may fail in some contexts (e.g., private browsing)
      return false;
    }
  }

  return false;
}

/**
 * Debug logging function that only logs in development mode or when explicitly enabled.
 * Drop-in replacement for console.log with the same signature.
 *
 * @param args - Arguments to log (same as console.log)
 */
export function debugLog(...args: unknown[]): void {
  if (isDebugEnabled()) {
    console.log(...args);
  }
}
